import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import { useEventTracking } from "hooks/useEventTracking";
import { CourseChapter, CourseProgressFlow } from "types";
import { Play } from "lucide-react";
import { buildFlowPath } from "utils/flowNavigation";

interface ContinueCourseSectionProps {
  courseFlows: CourseProgressFlow[] | null;
  courseName?: string;
  courseId?: number | null;
  isProgressUnavailable?: boolean;
  hasLockedCourses?: boolean;
  chapters?: CourseChapter[];
  onRetry?: (() => Promise<unknown> | void) | null;
}

interface ProgressItem {
  id: string;
  label: string;
}

const DEFAULT_COURSE_NAME = "NO COURSE";

export const ContinueCourseSection: React.FC<ContinueCourseSectionProps> = ({
  courseFlows,
  courseName = DEFAULT_COURSE_NAME,
  courseId = null,
  isProgressUnavailable = false,
  hasLockedCourses = false,
  chapters,
  onRetry = null,
}) => {
  const navigate = useNavigate();
  const { track } = useEventTracking();

  const orderedCourseFlows = useMemo<CourseProgressFlow[]>(() => {
    if (!courseFlows?.length) {
      return [];
    }

    return courseFlows
      .map((flow, index) => ({ flow, index }))
      .sort((a, b) => {
        const aOrder = typeof a.flow.order === "number" ? a.flow.order : Number.MAX_SAFE_INTEGER;
        const bOrder = typeof b.flow.order === "number" ? b.flow.order : Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        return a.index - b.index;
      })
      .map(({ flow }) => flow);
  }, [courseFlows]);

  const displayOrderByFlowId = useMemo(() => {
    const orderMap = new Map<number, number>();
    orderedCourseFlows.forEach((flow, index) => {
      orderMap.set(flow.id, index + 1);
    });
    return orderMap;
  }, [orderedCourseFlows]);

  const incompleteCourseFlows = useMemo<CourseProgressFlow[]>(() => {
    if (!orderedCourseFlows.length) {
      return [];
    }

    return orderedCourseFlows.filter((flow) => !flow.completed_at);
  }, [orderedCourseFlows]);

  const courseFlow = incompleteCourseFlows[0] ?? null;

  const progressItems = useMemo<ProgressItem[]>(() => {
    if (!incompleteCourseFlows.length) {
      return [];
    }

    return incompleteCourseFlows.slice(1, 4).map((flow, index) => {
      const flowName = flow.flow_details?.title || flow.flow_details?.name || "Upcoming module";
      const displayOrder = displayOrderByFlowId.get(flow.id) ?? index + 2;

      const chapter = chapters?.find((item) => item.id === flow.chapter_id);
      return {
        id: `${flow.id}-${flow.flow_details?.name ?? index}`,
        label: `${displayOrder}. ${flowName}${chapter?.title ? ` · ${chapter.title}` : flow.flow_details?.is_quiz ? ' · Quiz' : ''}`
      };
    });
  }, [displayOrderByFlowId, incompleteCourseFlows, chapters]);

  const handleBrowseContent = () => {
    navigate("/training#courses");
  };

  const handleRetry = () => {
    void onRetry?.();
  };

  const isCurrentModuleLocked = Boolean(courseFlow?.is_locked || courseFlow?.requirements_met === false);

  const handlePrimaryAction = () => {
    if (isProgressUnavailable) {
      handleRetry();
      return;
    }

    if (!courseFlow) {
      handleBrowseContent();
      return;
    }

    if (isCurrentModuleLocked) {
      navigate(courseId ? `/training?courseId=${courseId}#courses` : "/training#courses");
      return;
    }

    const flowName = courseFlow.flow_details?.name;
    if (!flowName) {
      return;
    }

    track("button_click", {
      action: "continue_course_button",
      course_name: flowName,
      course_id: courseId
    });

    navigate(buildFlowPath({ flowName, courseId }));
  };

  const hasCourse = Boolean(courseFlow);
  const headerLabel = isProgressUnavailable
    ? "CONTINUE COURSE"
    : hasCourse
      ? courseName
      : "BROWSE AVAILABLE COURSES";

  const hasStartedCurrentFlow = courseFlow?.has_started ?? false;

  const ctaLabel = isProgressUnavailable
    ? "Try Again"
    : !hasCourse && hasLockedCourses
      ? 'View prerequisites'
      : !hasCourse
      ? 'Browse Content'
      : isCurrentModuleLocked
        ? 'View requirements'
        : hasStartedCurrentFlow === false
          ? 'Begin'
          : 'Continue';

  const cardTitle = useMemo(() => {
    if (isProgressUnavailable) {
      return "We couldn't load your progress.";
    }

    if (!courseFlow) {
      return hasLockedCourses ? "Your next course has prerequisites" : "You're all caught up!";
    }

    const flowName = courseFlow.flow_details?.title || courseFlow.flow_details?.name || "Untitled module";
    const displayOrder = displayOrderByFlowId.get(courseFlow.id) ?? 1;
    return `${displayOrder}. ${flowName}`;
  }, [courseFlow, displayOrderByFlowId, isProgressUnavailable, hasLockedCourses]);

  const currentChapter = chapters?.find((chapter) => chapter.id === courseFlow?.chapter_id);
  const displayOrder = courseFlow ? displayOrderByFlowId.get(courseFlow.id) ?? 1 : 0;
  // Keep a readable window around the current module on long courses.
  const routeStart = Math.max(0, Math.min(displayOrder - 3, orderedCourseFlows.length - 8));
  const routeFlows = orderedCourseFlows.slice(routeStart, routeStart + 8);
  const routeHeights = [104, 88, 94, 64, 76, 40, 48, 12];
  const routePoints = routeFlows.map((flow, index) => ({
    flow,
    number: routeStart + index + 1,
    x: routeFlows.length === 1 ? 450 : 55 + index * 790 / (routeFlows.length - 1),
    y: routeFlows.length === 1 ? 76 : routeHeights[Math.round(index * 7 / (routeFlows.length - 1))],
    state: flow.id === courseFlow?.id ? 'current' : flow.completed_at ? 'completed' : 'upcoming',
  }));
  const routeLine = routePoints.length
    ? `M 0 ${routePoints[0].y + 7} ${routePoints.map((point) => `L ${point.x} ${point.y}`).join(' ')} L 900 ${routePoints[routePoints.length - 1].y - 6}`
    : '';

  return (
    <section className="home-learning" aria-label="Continue course">
      <div className="home-learning__main">
        <div className="home-learning__content">
          <p className="home-eyebrow">{headerLabel}{currentChapter?.title ? ` · ${currentChapter.title}` : ''}</p>
          <h2>{cardTitle}</h2>
          {hasCourse && !isProgressUnavailable ? <p className="home-learning__position">Module {displayOrder} of {orderedCourseFlows.length}</p> : null}
          {isProgressUnavailable ? <p>Try again or browse your training library.</p> : !hasCourse && hasLockedCourses ? <p>Complete the listed prerequisites to unlock your assigned courses. Your progress is saved.</p> : null}
        </div>
        <div className="home-learning__actions">
          <Button onClick={handlePrimaryAction} className="home-primary">
            {hasCourse && !isProgressUnavailable ? <Play className="h-4 w-4" aria-hidden="true" /> : null}
            {ctaLabel}
          </Button>
          {isProgressUnavailable ? <Button variant="secondary" onClick={handleBrowseContent}>Browse Content</Button> : null}
        </div>
      </div>
      {!isProgressUnavailable && progressItems.length > 0 ? (
        <div className="home-learning__up-next">
          <span className="home-eyebrow">Up next</span>
          {progressItems.map((item) => (
            <div key={item.id} className="min-w-0 w-full home-learning__upcoming" data-testid="upcoming-module-row">
              <span className="min-w-0 flex-1 break-words sm:truncate" title={item.label}>{item.label}</span>
            </div>
          ))}
        </div>
      ) : null}
      {hasCourse && !isProgressUnavailable ? (
        <div className="home-learning__route" aria-hidden="true" data-testid="home-course-route">
          <div className="home-learning__route-chart">
            <svg viewBox="0 0 900 126" preserveAspectRatio="none" fill="none">
              <path className="home-learning__route-area" d={`${routeLine} L 900 126 L 0 126 Z`} />
              <path className="home-learning__route-line" d={routeLine} vectorEffect="non-scaling-stroke" />
            </svg>
            {routePoints.map((point) => <span key={point.flow.id}
              className={`home-learning__route-node is-${point.state}`}
              data-testid={`home-course-route-node-${point.number}`} data-state={point.state}
              style={{ left: `${point.x / 9}%`, top: `${point.y / 1.26}%` }} />)}
          </div>
          <div className="home-learning__route-labels">
            {routePoints.map((point) => <span key={point.flow.id} className={point.state === 'current' ? 'is-current' : undefined}
              style={{ left: `${point.x / 9}%` }}>{point.number}</span>)}
          </div>
        </div>
      ) : null}
    </section>
  );
};
